export const READMORE = {
  init() {
    Readmore.initAll('[data-readmore]')
  },
}

class Readmore {
  static _uid = 0
  static _instances = []
  static _resizeBound = false

  static nextUid() {
    Readmore._uid += 1
    return Readmore._uid
  }

  static initAll(selector = '[data-readmore]') {
    const roots = document.querySelectorAll(selector)

    const created = Array.from(roots)
      .map(root => {
        if (root.dataset.readmoreInitialized === 'true') return null
        root.dataset.readmoreInitialized = 'true'

        const ins = new Readmore(root)
        ins.init()
        Readmore._instances.push(ins)
        return ins
      })
      .filter(Boolean)

    if (!Readmore._resizeBound) {
      Readmore._resizeBound = true
      let tid = null
      window.addEventListener('resize', () => {
        clearTimeout(tid)
        tid = setTimeout(() => {
          Readmore._instances.forEach(i => i.requestEvaluate())
        }, 120)
      })
    }

    return created
  }

  constructor(root) {
    this.root = root
    this.uid = Readmore.nextUid()

    this.linesDesktop = Number(root.dataset.linesDesktop || 2)
    this.linesMobile = Number(root.dataset.linesMobile || 3)
    this.breakpoint = Number(root.dataset.breakpoint || 768)

    this.labelOpen = root.dataset.labelOpen || '続きを読む'
    this.labelClose = root.dataset.labelClose || '閉じる'

    this._evalLock = false
    this._rafEval = 0
    this._skipNextRO = false
    this._cooldownUntil = 0

    this._animTid = 0
    this._onAnimEnd = null

    this.onClick = this.onClick.bind(this)
  }

  init() {
    this.titleEl = this.root.querySelector('.c-readmore__title')
    this.contentEl = this.root.querySelector('.c-readmore__content')
    this.btnEl = this.root.querySelector('.c-readmore__btn')
    if (!this.titleEl || !this.contentEl || !this.btnEl) return

    if (!this.contentEl.id) this.contentEl.id = `readmore-${this.uid}-content`
    this.btnEl.type = 'button'
    this.btnEl.setAttribute('aria-controls', this.contentEl.id)
    this.btnEl.setAttribute('aria-expanded', 'false')

    this.normalizeContent()

    this.btnEl.addEventListener('click', this.onClick)

    if (window.ResizeObserver) {
      this.ro = new ResizeObserver(() => {
        if (this._skipNextRO) {
          this._skipNextRO = false
          return
        }
        if (this._evalLock) return

        const now = performance?.now?.() ?? Date.now()
        if (now < this._cooldownUntil) return

        if (this.root.classList.contains('is-animating')) return

        this.requestEvaluate()
      })

      this.ro.observe(this.contentEl)
    }

    this.evaluate()
    requestAnimationFrame(() => this.requestEvaluate())
    window.addEventListener('load', () => this.requestEvaluate(), { once: true })
  }

  // reflow 強制（no-void対応）
  forceReflow(el) {
    // eslint / jslint 対策：値を読む
    // （戻り値は使わないが、読み取りでレイアウト確定する）
    const _reflow = el.offsetHeight // eslint-disable-line no-unused-vars
    return _reflow
  }

  isMobile() {
    return window.innerWidth < this.breakpoint
  }

  getLines() {
    return this.isMobile() ? this.linesMobile : this.linesDesktop
  }

  prefersReducedMotion() {
    return !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
  }

  normalizeContent() {
    this.summaryEl = this.contentEl.querySelector('.c-readmore__summary')
    this.moreEl = this.contentEl.querySelector('.c-readmore__more')
    if (this.summaryEl && this.moreEl) return

    const children = Array.from(this.contentEl.children)

    if (children.length === 0) {
      const wrap = document.createElement('div')
      wrap.className = 'c-readmore__summary'
      while (this.contentEl.firstChild) wrap.appendChild(this.contentEl.firstChild)
      this.contentEl.appendChild(wrap)
      this.summaryEl = wrap
    } else {
      this.summaryEl = children[0]
      this.summaryEl.classList.add('c-readmore__summary')
    }

    const more = document.createElement('div')
    more.className = 'c-readmore__more'

    let node = this.summaryEl.nextSibling
    while (node) {
      const next = node.nextSibling
      more.appendChild(node)
      node = next
    }

    if (more.childNodes.length > 0) {
      this.contentEl.appendChild(more)
      this.moreEl = more
    } else {
      this.moreEl = null
    }
  }

  setEnabled(enabled) {
    this.root.classList.toggle('is-enabled', enabled)
    if (!enabled) {
      this.root.classList.remove('is-open')
      this.btnEl.setAttribute('aria-expanded', 'false')
    }
  }

  requestEvaluate() {
    if (this._rafEval) return
    this._rafEval = requestAnimationFrame(() => {
      this._rafEval = 0
      this.evaluate()
    })
  }

  measureHeights(lines) {
    this.root.style.setProperty('--readmore-lines', String(lines))

    const s = this.summaryEl.style
    const m = this.moreEl?.style
    const c = this.contentEl.style

    const prev = {
      s_display: s.display,
      s_overflow: s.overflow,
      s_webkitBoxOrient: s.webkitBoxOrient,
      s_webkitLineClamp: s.webkitLineClamp,
      s_paddingBottom: s.paddingBottom,
      m_display: m ? m.display : null,
      c_height: c.height,
    }

    c.height = ''

    // collapsed
    s.display = '-webkit-box'
    s.webkitBoxOrient = 'vertical'
    s.webkitLineClamp = String(lines)
    s.overflow = 'hidden'
    s.paddingBottom = '0.12em'
    if (this.moreEl) this.moreEl.style.display = 'none'

    this.forceReflow(this.contentEl)

    const collapsedH =
      this.titleEl.getBoundingClientRect().height + this.contentEl.getBoundingClientRect().height

    // open
    s.display = 'block'
    s.webkitLineClamp = 'unset'
    s.webkitBoxOrient = ''
    s.overflow = 'visible'
    s.paddingBottom = ''
    if (this.moreEl) this.moreEl.style.display = 'block'

    this.forceReflow(this.contentEl)

    const fullH =
      this.titleEl.getBoundingClientRect().height + this.contentEl.getBoundingClientRect().height

    // restore
    s.display = prev.s_display
    s.overflow = prev.s_overflow
    s.webkitBoxOrient = prev.s_webkitBoxOrient
    s.webkitLineClamp = prev.s_webkitLineClamp
    s.paddingBottom = prev.s_paddingBottom
    if (this.moreEl) this.moreEl.style.display = prev.m_display
    c.height = prev.c_height

    return { collapsedH, fullH }
  }

  getVisualContentHeightPxForState(open) {
    const lines = this.getLines()
    this.root.style.setProperty('--readmore-lines', String(lines))

    const s = this.summaryEl.style
    const m = this.moreEl?.style
    const c = this.contentEl.style

    const prev = {
      s_display: s.display,
      s_overflow: s.overflow,
      s_webkitBoxOrient: s.webkitBoxOrient,
      s_webkitLineClamp: s.webkitLineClamp,
      s_paddingBottom: s.paddingBottom,
      m_display: m ? m.display : null,
      c_height: c.height,
    }

    c.height = ''

    if (open) {
      s.display = 'block'
      s.webkitLineClamp = 'unset'
      s.webkitBoxOrient = ''
      s.overflow = 'visible'
      s.paddingBottom = ''
      if (this.moreEl) this.moreEl.style.display = 'block'
    } else {
      s.display = '-webkit-box'
      s.webkitBoxOrient = 'vertical'
      s.webkitLineClamp = String(lines)
      s.overflow = 'hidden'
      s.paddingBottom = '0.12em'
      if (this.moreEl) this.moreEl.style.display = 'none'
    }

    this.forceReflow(this.contentEl)

    const h = this.contentEl.getBoundingClientRect().height

    // restore
    s.display = prev.s_display
    s.overflow = prev.s_overflow
    s.webkitBoxOrient = prev.s_webkitBoxOrient
    s.webkitLineClamp = prev.s_webkitLineClamp
    s.paddingBottom = prev.s_paddingBottom
    if (this.moreEl) this.moreEl.style.display = prev.m_display
    c.height = prev.c_height

    return h
  }

  evaluate() {
    if (this.root.classList.contains('is-animating')) return
    if (this._evalLock) return
    this._evalLock = true

    const hadRO = !!this.ro
    if (hadRO) this.ro.disconnect()

    const lines = this.getLines()
    const { collapsedH, fullH } = this.measureHeights(lines)

    const EPS = 2
    const enable = fullH > collapsedH + EPS

    this.setEnabled(enable)
    this.root.dataset.readmoreReady = 'true'

    if (enable) {
      const isOpen = this.root.classList.contains('is-open')
      this.btnEl.setAttribute('aria-expanded', String(isOpen))
      this.btnEl.textContent = isOpen ? this.labelClose : this.labelOpen
    }

    if (hadRO) {
      this._skipNextRO = true
      this.ro.observe(this.contentEl)
    }

    this._evalLock = false
  }

  setOpen(open) {
    if (!this.root.classList.contains('is-enabled')) {
      this.root.classList.toggle('is-open', open)
      this.btnEl.setAttribute('aria-expanded', String(open))
      this.btnEl.textContent = open ? this.labelClose : this.labelOpen
      return
    }

    const isCurrentlyOpen = this.root.classList.contains('is-open')
    if (open === isCurrentlyOpen) return

    if (this.prefersReducedMotion()) {
      this.root.classList.toggle('is-open', open)
      this.btnEl.setAttribute('aria-expanded', String(open))
      this.btnEl.textContent = open ? this.labelClose : this.labelOpen
      return
    }

    const targetH = this.getVisualContentHeightPxForState(open)

    if (this._animTid) {
      clearTimeout(this._animTid)
      this._animTid = 0
    }
    if (this._onAnimEnd) {
      this.contentEl.removeEventListener('transitionend', this._onAnimEnd)
      this._onAnimEnd = null
    }

    const now = performance?.now?.() ?? Date.now()
    this._cooldownUntil = now + 380

    const startH = this.contentEl.getBoundingClientRect().height

    this.root.classList.add('is-animating')
    this.contentEl.style.height = `${startH}px`

    this.forceReflow(this.contentEl)

    this.root.classList.toggle('is-open', open)
    this.btnEl.setAttribute('aria-expanded', String(open))
    this.btnEl.textContent = open ? this.labelClose : this.labelOpen

    this.contentEl.style.height = `${targetH}px`

    const finish = () => {
      this.root.classList.remove('is-animating')
      this.contentEl.style.height = ''

      if (this._onAnimEnd) {
        this.contentEl.removeEventListener('transitionend', this._onAnimEnd)
        this._onAnimEnd = null
      }
    }

    this._onAnimEnd = e => {
      if (e.target !== this.contentEl) return
      finish()
    }

    this.contentEl.addEventListener('transitionend', this._onAnimEnd)
    this._animTid = setTimeout(finish, 520)
  }

  onClick() {
    if (!this.root.classList.contains('is-enabled')) return
    const next = !this.root.classList.contains('is-open')
    this.setOpen(next)
  }
}
